// @flow

import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { ProductIcon, productToTheme, type ProductType } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';

import { selectGostackURL } from '../../modules/config';

import styles from './ProductTile.scss';

// Types needed in app
export type Type =
  | 'domain'
  | 'certificate'
  | 'paas'
  | 'cloud'
  | 'mailbox-standalone';

type Props = {
  type: Type,
};

function useProductNames(type: Type) {
  const { __ } = useTranslator();
  const names = {
    domain: __('Domain'),
    certificate: __('SSL Certificate'),
    paas: __('Web Hosting'),
    cloud: __('Cloud'),
    'mailbox-standalone': __('Mailbox'),
  };
  return names[type];
}

function ProductLink({
  type,
  children,
}: {
  type: Type,
  children: React.Node,
}): React.MixedElement {
  const title = useProductNames(type);
  const gostackUrl = useAppSelector(selectGostackURL);
  const links: { [ProductType]: { to: string } | { href: string } } = {
    domain: { to: `/domain/suggest` },
    certificate: { to: `/certificate/create` },
    paas: { to: `/simplehosting/create` },
    cloud: { href: gostackUrl },
    'mailbox-standalone': { to: `/mailboxes/create` },
  };

  const props = {
    title,
    className: clsx(styles[productToTheme[type]], styles.productBlock),
  };

  const linkType = links[type];

  if (linkType.to) {
    return (
      <Link {...props} to={linkType.to}>
        {children}
      </Link>
    );
  }

  return (
    <a {...props} href={linkType.href}>
      {children}
    </a>
  );
}

export function ProductTile({ type }: Props): React.MixedElement | null {
  const FF_APP_MAIL_BETA = useFeatureFlag('FF_APP_MAIL_BETA');
  const productName = useProductNames(type);

  if (!productName || (type === 'mailbox-standalone' && !FF_APP_MAIL_BETA))
    return null;

  return (
    <ProductLink type={type}>
      <ProductIcon type={type} />
      <p>{productName}</p>
    </ProductLink>
  );
}
